import { useMemo, useState } from "react";
import { occurrenceApi, type OccurrenceCreateResponseApi } from "@/lib/api";
import { OCCURRENCE_TYPE_LABELS, OCCURRENCE_TYPE_OPTIONS, OCCURRENCE_STATUS } from "@/config/occurrence-config";

interface Props {
  treeId: number;
  codigoCta: string;
  latitude: number | null;
  longitude: number | null;
  projectId: number | null;
  street: string | null;
  neighborhood: string | null;
  onClose: () => void;
  /** Chamado apos o registro, para a ficha recarregar a lista de ocorrencias. */
  onCreated: () => void;
}

type Impact = boolean | null;

const IMPACTS: { key: "blocks_road" | "hits_power_grid" | "has_property_damage" | "has_victims"; label: string }[] = [
  { key: "blocks_road", label: "Obstrui a via ou a calçada?" },
  { key: "hits_power_grid", label: "Atingiu a rede elétrica?" },
  { key: "has_property_damage", label: "Causou dano a imóvel ou veículo?" },
  { key: "has_victims", label: "Há pessoas feridas?" },
];

/** Tres estados: "Não informado" nao afirma que nao houve impacto. */
function ImpactChoice({ label, value, onChange }: { label: string; value: Impact; onChange: (v: Impact) => void }) {
  const options: { text: string; v: Impact }[] = [
    { text: "Não sei", v: null },
    { text: "Não", v: false },
    { text: "Sim", v: true },
  ];
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 py-1.5">
      <span className="text-sm text-foreground-700">{label}</span>
      <div role="radiogroup" aria-label={label} className="inline-flex rounded-lg border border-background-200 overflow-hidden self-start">
        {options.map((o) => {
          const selected = value === o.v;
          const tone = !selected
            ? "bg-white text-foreground-500 hover:bg-background-50"
            : o.v === true
              ? "bg-warning-100 text-warning-800 font-semibold"
              : o.v === false
                ? "bg-primary-100 text-primary-800 font-semibold"
                : "bg-background-100 text-foreground-700 font-medium";
          return (
            <button
              key={o.text}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(o.v)}
              className={`px-3 py-1.5 text-xs border-l first:border-l-0 border-background-200 ${tone}`}
            >
              {o.text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function RegisterOccurrenceModal(props: Props) {
  const { treeId, codigoCta, latitude, longitude, onClose, onCreated } = props;
  // Uma operacao por abertura do formulario: um duplo clique ou reenvio nao cria duas ocorrencias.
  const operationId = useMemo(() => crypto.randomUUID(), []);
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [impacts, setImpacts] = useState<Record<string, Impact>>({});
  const [emergency, setEmergency] = useState<Impact>(null);
  const [emergencyText, setEmergencyText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OccurrenceCreateResponseApi | null>(null);

  const hasCoords = latitude !== null && longitude !== null;
  const canSubmit =
    hasCoords && type !== "" && description.trim().length >= 3 && (emergency !== true || emergencyText.trim().length > 0) && !busy;

  async function submit() {
    if (!canSubmit || latitude === null || longitude === null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await occurrenceApi.create({
        occurrence_type: type,
        description: description.trim(),
        channel: "SITE",
        // A ocorrencia nasce na posicao cadastrada do exemplar.
        latitude,
        longitude,
        positioning_method: "EXEMPLAR_CADASTRADO",
        occurred_at: occurredAt ? new Date(occurredAt).toISOString() : null,
        street: props.street,
        neighborhood: props.neighborhood,
        project_id: props.projectId,
        blocks_road: impacts.blocks_road ?? null,
        hits_power_grid: impacts.hits_power_grid ?? null,
        has_property_damage: impacts.has_property_damage ?? null,
        has_victims: impacts.has_victims ?? null,
        emergency_action_taken: emergency,
        emergency_action_description: emergency ? emergencyText.trim() : null,
        trees: [{ tree_id: treeId, link_role: "PRINCIPAL" }],
        client_operation_id: operationId,
      });
      setResult(res);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível registrar a ocorrência.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="oc-title">
      <div className="bg-white w-full sm:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-card shadow-xl">
        <div className="sticky top-0 bg-white border-b border-background-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h2 id="oc-title" className="text-base font-semibold text-foreground-900 font-heading">Registrar ocorrência</h2>
            <p className="text-xs text-foreground-500">Exemplar {codigoCta}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center" aria-label="Fechar">
            <i className="ri-close-line text-lg" aria-hidden="true"></i>
          </button>
        </div>

        {result ? (
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3 p-4 rounded-lg bg-primary-50 border border-primary-200">
              <i className="ri-checkbox-circle-line text-2xl text-primary-600" aria-hidden="true"></i>
              <div>
                <p className="text-sm font-semibold text-foreground-900">
                  {result.replayed ? "Esta ocorrência já tinha sido registrada." : "Ocorrência registrada."}
                </p>
                <p className="text-sm text-foreground-700 mt-0.5">
                  Protocolo <strong className="font-mono">{result.occurrence.protocol}</strong>
                </p>
                <p className="text-xs text-foreground-600 mt-1">
                  A triagem define a prioridade e o responsável. A situação do exemplar só muda depois que um analista confirmar a ocorrência.
                </p>
              </div>
            </div>
            {result.possible_duplicates.length > 0 && (
              <div className="p-4 rounded-lg bg-warning-50 border border-warning-200">
                <p className="text-sm font-semibold text-warning-800">Pode ser a mesma ocorrência de outra já aberta</p>
                <p className="text-xs text-warning-800/90 mt-0.5">A triagem vai conferir. Não é preciso registrar de novo.</p>
                <ul className="mt-2 space-y-1">
                  {result.possible_duplicates.map((d) => (
                    <li key={d.id} className="text-xs text-foreground-700">
                      <span className="font-mono">{d.protocol}</span> — {OCCURRENCE_TYPE_LABELS[d.occurrence_type] ?? d.occurrence_type},{" "}
                      {OCCURRENCE_STATUS[d.status]?.label ?? d.status}, a {d.distance_m.toFixed(0)} m
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button type="button" onClick={onClose} className="btn-primary text-sm w-full justify-center">Fechar</button>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {!hasCoords && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                Este exemplar não tem coordenada cadastrada. Corrija a localização em “Editar cadastro” antes de registrar a ocorrência.
              </p>
            )}

            <fieldset>
              <legend className="text-sm font-semibold text-foreground-900 mb-2">O que aconteceu? *</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {OCCURRENCE_TYPE_OPTIONS.map((o) => (
                  <label
                    key={o.code}
                    className={`flex gap-2 p-2.5 rounded-lg border cursor-pointer ${
                      type === o.code ? "border-primary-500 bg-primary-50" : "border-background-200 hover:bg-background-50"
                    }`}
                  >
                    <input type="radio" name="oc-type" value={o.code} checked={type === o.code} onChange={() => setType(o.code)} className="mt-0.5" />
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">{o.label}</span>
                      <span className="block text-[11px] text-foreground-500 leading-snug">{o.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <label htmlFor="oc-desc" className="text-sm font-semibold text-foreground-900">Descreva o que foi visto *</label>
              <textarea
                id="oc-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={5000}
                placeholder="Ex.: galho de cerca de 3 m caído sobre a calçada, rachadura na base do tronco."
                className="mt-1 w-full rounded-lg border border-background-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div>
              <label htmlFor="oc-when" className="text-sm font-semibold text-foreground-900">Quando aconteceu?</label>
              <p className="text-[11px] text-foreground-500">Deixe em branco se não souber. O registro grava a data de hoje de qualquer forma.</p>
              <input
                id="oc-when"
                type="datetime-local"
                value={occurredAt}
                max={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                onChange={(e) => setOccurredAt(e.target.value)}
                className="mt-1 rounded-lg border border-background-200 px-3 py-2 text-sm"
              />
            </div>

            <fieldset>
              <legend className="text-sm font-semibold text-foreground-900">Impactos</legend>
              <p className="text-[11px] text-foreground-500 mb-1">Marque só o que foi verificado. “Não sei” não é o mesmo que “Não”.</p>
              {IMPACTS.map((i) => (
                <ImpactChoice
                  key={i.key}
                  label={i.label}
                  value={impacts[i.key] ?? null}
                  onChange={(v) => setImpacts((prev) => ({ ...prev, [i.key]: v }))}
                />
              ))}
              <ImpactChoice label="Foi tomada alguma ação emergencial?" value={emergency} onChange={setEmergency} />
              {emergency === true && (
                <textarea
                  value={emergencyText}
                  onChange={(e) => setEmergencyText(e.target.value)}
                  rows={2}
                  placeholder="Qual ação? Ex.: área isolada com fita, Defesa Civil acionada."
                  aria-label="Descrição da ação emergencial"
                  className="mt-1 w-full rounded-lg border border-background-200 px-3 py-2 text-sm"
                />
              )}
            </fieldset>

            {error && (
              <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
            )}

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
              <button type="button" onClick={onClose} className="btn-outline text-sm justify-center">Cancelar</button>
              <button type="button" onClick={submit} disabled={!canSubmit} className="btn-primary text-sm justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                <i className={busy ? "ri-loader-4-line animate-spin" : "ri-alarm-warning-line"} aria-hidden="true"></i>
                {busy ? "Registrando..." : "Registrar ocorrência"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
